import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Select from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { Trash2, Clock, SlidersHorizontal, Plus } from 'lucide-react';
import axios from '../../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { brandSelectTheme } from '../../../Common/reactSelectTheme';
import InvoiceTimeImportModal from './InvoiceTimeImportModal';
import { calculateInvoiceTotals, lineAmount, formatMoney, parseVatRate } from './invoiceTotals';

/**
 * Line items: one row per line, edited in place.
 *
 * "Ny rad" adds a row whose Beskrivning cell is a searchable dropdown over
 * the article register. Picking an article fills the row; typing something
 * that is not in the register makes it a free row with that description,
 * and the user completes the rest of the cells. Once a row has a
 * description the cell is a plain text input.
 *
 * Artikelkod and artikeltyp are secondary; the sliders button opens them
 * as a second line under the row.
 */

const UNIT_OPTIONS = [
    { value: 'Piece', label: 'st' },
    { value: 'Hour', label: 'tim' },
    { value: 'Day', label: 'dag' },
    { value: 'Month', label: 'mån' },
    { value: 'Package', label: 'frp' },
    { value: 'Set', label: 'set' },
    { value: 'Kilogram', label: 'kg' },
    { value: 'Liter', label: 'l' },
    { value: 'Meter', label: 'm' },
    { value: 'Square Meter', label: 'm²' },
    { value: 'Cubic Meter', label: 'm³' },
];

// Legacy rows store Swedish short units; map them onto the option values so
// the select does not render blank.
const LEGACY_UNITS = { st: 'Piece', tim: 'Hour', dag: 'Day', mån: 'Month', frp: 'Package', kg: 'Kilogram', m: 'Meter', Kg: 'Kilogram' };
const normalizeUnit = (unit) => LEGACY_UNITS[unit] || unit || 'Piece';

const TYPE_OPTIONS = [
    { value: 'Goods', label: 'Vara' },
    { value: 'Service', label: 'Tjänst' },
    { value: 'Text', label: 'Textrad' },
];
// The article register writes 'Product'; the editor has always offered
// Goods/Service/Text. Treat 'Product' as Goods.
const normalizeType = (type) => (type === 'Product' || !type ? 'Goods' : type);

const VAT_OPTIONS = [0, 6, 12, 25].map((rate) => ({ value: String(rate), label: `${rate} %` }));

// react-select inside a table cell, sized like .inv-cell. Menus render in a
// portal because the table's scroll wrapper would clip them.
const cellSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 34,
        height: 34,
        borderRadius: 6,
        borderColor: state.isFocused ? '#44814E' : '#e5e7eb',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(68, 129, 78, 0.10)' : 'none',
        fontSize: '0.875rem',
        '&:hover': { borderColor: state.isFocused ? '#44814E' : '#d1d5db' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: 32 }),
    singleValue: (base) => ({ ...base, color: '#111827' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    placeholder: (base) => ({ ...base, color: '#9ca3af' }),
    indicatorSeparator: (base) => ({ ...base, marginTop: 7, marginBottom: 7 }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 6px', color: '#6b7280' }),
    menuPortal: (base) => ({ ...base, zIndex: 1060 }),
    menu: (base) => ({ ...base, borderRadius: 8, minWidth: '100%' }),
    option: (base) => ({ ...base, fontSize: '0.875rem', padding: '8px 12px' }),
};

// Narrow columns (Enhet, Moms): same control, tighter, no typing.
const compactSelectStyles = {
    ...cellSelectStyles,
    valueContainer: (base) => ({ ...base, padding: '0 6px', height: 32 }),
    dropdownIndicator: (base) => ({ ...base, padding: '0 4px', color: '#6b7280' }),
    menu: (base) => ({ ...base, borderRadius: 8, minWidth: '7rem' }),
};

const descriptionMenuStyles = {
    ...cellSelectStyles,
    menu: (base) => ({ ...base, borderRadius: 8, minWidth: '28rem' }),
};

const portalTarget = typeof document !== 'undefined' ? document.body : null;

const InvoiceItemsTable = ({
    items = [],
    onItemsChange,
    availableArticles = [],
    roundTotal = false,
    defaultVatRate = 25,
    currency = 'SEK',
    vatTreatment = 'taxable',
    companyId,
    customerId,
    // Rendered bottom-left, opposite the totals (the invoice note).
    footerStart = null,
}) => {
    // For non-taxable invoices (omvänd skattskyldighet, undantag, utanför
    // tillämpningsområdet) the seller charges no output VAT — rows keep their
    // stored tax_rate but it is neither shown nor summed.
    const isTaxable = vatTreatment === 'taxable';
    const defaultRate = `${parseVatRate(defaultVatRate) || 25}%`;

    const [expandedRows, setExpandedRows] = useState(() => new Set());
    const [showTimeImport, setShowTimeImport] = useState(false);
    const [timeImportPreview, setTimeImportPreview] = useState(null);
    const [timeImportLoading, setTimeImportLoading] = useState(false);
    const [timeImportError, setTimeImportError] = useState(null);
    // Entry ids already pulled in this session, so the same hours cannot be
    // imported twice onto one invoice.
    const [importedEntryIds, setImportedEntryIds] = useState(() => new Set());

    // After adding a row, put the cursor where typing continues: the
    // description when it is empty, otherwise the quantity.
    const focusRowId = useRef(null);
    const quantityRefs = useRef({});
    const descriptionRefs = useRef({});
    useEffect(() => {
        const id = focusRowId.current;
        if (!id) return;
        const row = items.find((item) => item.id === id);
        const target = row && !row.description ? descriptionRefs.current[id] : quantityRefs.current[id];
        if (target) {
            target.focus();
            if (target.select && target.tagName === 'INPUT') target.select();
        }
        focusRowId.current = null;
    }, [items]);

    const money = useCallback((value) => formatMoney(value, currency), [currency]);

    // ── Add line (article search or free text) ────────────────────────────
    const articleOption = useCallback((article) => ({
        value: article.id,
        label: article.description || article.productcode || '',
        meta: [article.productcode, money(article.unitprice || 0)].filter(Boolean).join(' · '),
        article,
    }), [money]);

    const loadArticleOptions = useCallback((inputValue) => {
        const term = (inputValue || '').toLowerCase();
        const matches = term
            ? availableArticles.filter((a) => a.productcode?.toLowerCase().includes(term)
                || a.description?.toLowerCase().includes(term))
            : availableArticles;
        return Promise.resolve(matches.slice(0, 30).map(articleOption));
    }, [availableArticles, articleOption]);

    const defaultArticleOptions = useMemo(
        () => availableArticles.slice(0, 8).map(articleOption),
        [availableArticles, articleOption],
    );

    const rowFromArticle = useCallback((article) => ({
        id: uuidv4(),
        productcode: article.productcode || '',
        description: article.description || '',
        unitprice: parseFloat(article.unitprice || 0),
        quantity: 1,
        unit: normalizeUnit(article.unit),
        tax_rate: article.tax_rate || defaultRate,
        type: normalizeType(article.type),
        article_id: article.id,
        isNew: true,
    }), [defaultRate]);

    const blankRow = useCallback((description = '') => ({
        id: uuidv4(),
        productcode: '',
        description,
        unitprice: 0,
        quantity: 1,
        unit: 'Piece',
        tax_rate: defaultRate,
        type: 'Goods',
        article_id: null,
        isNew: true,
    }), [defaultRate]);

    const addRows = useCallback((rows) => {
        if (!rows.length) return;
        focusRowId.current = rows[rows.length - 1].id;
        onItemsChange([...items, ...rows]);
    }, [items, onItemsChange]);

    // Picking in a blank row's description cell: an article fills the row,
    // free text names it. Either way the cursor moves on to Antal.
    const applyPick = useCallback((index, option) => {
        if (!option) return;
        const id = items[index]?.id;
        if (option.__isNew__) {
            updateRowRef.current(index, { description: String(option.value || '').trim() });
        } else if (option.article) {
            const { id: _ignored, isNew, ...fields } = rowFromArticle(option.article);
            updateRowRef.current(index, fields);
        }
        focusRowId.current = id;
    }, [items, rowFromArticle]);

    const formatOptionLabel = useCallback((option) => (
        option.__isNew__ ? (
            <span className="inv-add-row__create">
                <Plus size={14} strokeWidth={2} />
                Lägg till rad ”{option.value}”
            </span>
        ) : option.article ? (
            <span className="inv-add-row__option">
                <span>{option.label}</span>
                {option.meta && <span className="inv-add-row__meta">{option.meta}</span>}
            </span>
        ) : (
            option.label
        )
    ), []);

    // ── Row editing ───────────────────────────────────────────────────────
    const updateRow = useCallback((index, patch) => {
        const next = [...items];
        next[index] = { ...next[index], ...patch, isModified: true };
        onItemsChange(next);
    }, [items, onItemsChange]);
    const updateRowRef = useRef(updateRow);
    updateRowRef.current = updateRow;

    const removeRow = useCallback((index) => {
        const removed = items[index];
        if (removed?._timeImportEntryIds?.length) {
            setImportedEntryIds((prev) => {
                const next = new Set(prev);
                removed._timeImportEntryIds.forEach((eid) => next.delete(eid));
                return next;
            });
        }
        onItemsChange(items.filter((_, idx) => idx !== index));
    }, [items, onItemsChange]);

    const toggleDetails = useCallback((id) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const totals = useMemo(
        () => calculateInvoiceTotals(items, { roundTotal, isTaxable }),
        [items, roundTotal, isTaxable],
    );

    const openTimeImport = () => {
        setTimeImportPreview(null);
        setTimeImportError(null);
        setShowTimeImport(true);
    };
    const canImportTime = Boolean(companyId && customerId);
    const columnCount = 6 + (isTaxable ? 1 : 0);

    return (
        <div>
            {canImportTime && (
                <InvoiceTimeImportModal
                    show={showTimeImport}
                    previewData={timeImportPreview}
                    loading={timeImportLoading}
                    error={timeImportError}
                    importing={false}
                    onClose={() => {
                        setShowTimeImport(false);
                        setTimeImportPreview(null);
                        setTimeImportError(null);
                    }}
                    onLoadPreview={async ({ fromDate, toDate, groupBy }) => {
                        setTimeImportLoading(true);
                        setTimeImportError(null);
                        try {
                            const res = await axios.get(
                                getApiUrl(`${companyId}/time-reporting/billable-entries`),
                                { params: { customerId, fromDate, toDate, groupBy }, withCredentials: true },
                            );
                            setTimeImportPreview(res.data);
                        } catch (err) {
                            setTimeImportError(err.response?.data?.message || 'Kunde inte ladda tidrapportdata');
                        } finally {
                            setTimeImportLoading(false);
                        }
                    }}
                    onConfirmImport={({ selectedGroups }) => {
                        if (!timeImportPreview?.groups) return;
                        const selectedKeys = new Set(selectedGroups.map((g) => g.groupKey));
                        const descOverrides = Object.fromEntries(
                            selectedGroups.filter((g) => g.description).map((g) => [g.groupKey, g.description]),
                        );
                        const groupsToImport = timeImportPreview.groups
                            .filter((g) => selectedKeys.has(g.groupKey))
                            .map((g) => ({ ...g, entryIds: (g.entryIds || []).filter((eid) => !importedEntryIds.has(eid)) }))
                            .filter((g) => g.entryIds.length > 0);

                        if (groupsToImport.length === 0) {
                            setTimeImportError('Alla valda poster är redan importerade på denna faktura.');
                            return;
                        }

                        const newlyImported = new Set(importedEntryIds);
                        groupsToImport.forEach((g) => g.entryIds.forEach((eid) => newlyImported.add(eid)));
                        setImportedEntryIds(newlyImported);

                        addRows(groupsToImport.map((group) => ({
                            id: uuidv4(),
                            article_id: group.articleId || '',
                            productcode: group.articleProductcode || '',
                            description: descOverrides[group.groupKey] || group.label,
                            _fromTimeImport: true,
                            _timeImportEntryIds: group.entryIds,
                            quantity: group.totalHours,
                            unitprice: group.billingRate,
                            tax_rate: group.taxRate || '25%',
                            type: 'Service',
                            unit: 'Hour',
                            isNew: true,
                        })));
                        setShowTimeImport(false);
                        setTimeImportPreview(null);
                    }}
                />
            )}

            {items.length === 0 && (
                <div className="inv-items-empty">Inga rader ännu.</div>
            )}

            <div className="inv-table-wrap" hidden={items.length === 0}>
                <table className="inv-table">
                    <thead>
                        <tr>
                            <th>Beskrivning</th>
                            <th className="col-qty num">Antal</th>
                            <th className="col-unit">Enhet</th>
                            <th className="col-price num">À-pris</th>
                            {isTaxable && <th className="col-vat">Moms</th>}
                            <th className="col-amount num">Belopp</th>
                            <th className="col-actions"><span className="visually-hidden">Åtgärder</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const expanded = expandedRows.has(item.id);
                            const missingDescription = !item.description?.trim();
                            return (
                                <React.Fragment key={item.id}>
                                    <tr className={`inv-table__row${expanded ? ' is-expanded' : ''}`}>
                                        <td className="inv-table__desc">
                                            <div className={missingDescription ? 'inv-select-invalid' : undefined}>
                                            <AsyncCreatableSelect
                                                ref={(el) => { descriptionRefs.current[item.id] = el; }}
                                                aria-label={`Beskrivning rad ${index + 1}`}
                                                cacheOptions
                                                defaultOptions={defaultArticleOptions}
                                                loadOptions={loadArticleOptions}
                                                value={item.description ? { value: item.description, label: item.description } : null}
                                                onChange={(option) => applyPick(index, option)}
                                                formatOptionLabel={formatOptionLabel}
                                                isValidNewOption={(input) => Boolean(input && input.trim())}
                                                createOptionPosition="last"
                                                openMenuOnFocus={!item.description}
                                                placeholder={availableArticles.length > 0
                                                    ? 'Välj artikel eller skriv en beskrivning…'
                                                    : 'Skriv en beskrivning…'}
                                                noOptionsMessage={() => 'Skriv en beskrivning och tryck Enter'}
                                                loadingMessage={() => 'Söker…'}
                                                theme={brandSelectTheme}
                                                styles={descriptionMenuStyles}
                                                menuPortalTarget={portalTarget}
                                                menuPlacement="auto"
                                                classNamePrefix="react-select"
                                                title={item._fromTimeImport ? 'Importerad från tidrapport' : undefined}
                                            />
                                            </div>
                                        </td>
                                        <td className="col-qty">
                                            <input
                                                ref={(el) => { quantityRefs.current[item.id] = el; }}
                                                type="number"
                                                className="inv-cell num"
                                                aria-label={`Antal rad ${index + 1}`}
                                                min="0"
                                                step="any"
                                                value={item.quantity ?? ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '') { updateRow(index, { quantity: '' }); return; }
                                                    const parsed = parseFloat(value);
                                                    if (!Number.isNaN(parsed) && parsed >= 0) updateRow(index, { quantity: parsed });
                                                }}
                                                onBlur={(e) => {
                                                    const parsed = parseFloat(e.target.value);
                                                    if (Number.isNaN(parsed) || parsed <= 0) updateRow(index, { quantity: 1 });
                                                }}
                                            />
                                        </td>
                                        <td className="col-unit">
                                            <Select
                                                aria-label={`Enhet rad ${index + 1}`}
                                                options={UNIT_OPTIONS}
                                                value={UNIT_OPTIONS.find((u) => u.value === normalizeUnit(item.unit)) || UNIT_OPTIONS[0]}
                                                onChange={(opt) => updateRow(index, { unit: opt.value })}
                                                isSearchable={false}
                                                theme={brandSelectTheme}
                                                styles={compactSelectStyles}
                                                menuPortalTarget={portalTarget}
                                                menuPlacement="auto"
                                                classNamePrefix="react-select"
                                            />
                                        </td>
                                        <td className="col-price">
                                            <input
                                                type="number"
                                                className="inv-cell num"
                                                aria-label={`À-pris rad ${index + 1}`}
                                                min="0"
                                                step="0.01"
                                                value={item.unitprice ?? 0}
                                                onChange={(e) => updateRow(index, { unitprice: parseFloat(e.target.value) || 0 })}
                                            />
                                        </td>
                                        {isTaxable && (
                                            <td className="col-vat">
                                                <Select
                                                    aria-label={`Moms rad ${index + 1}`}
                                                    options={VAT_OPTIONS}
                                                    value={VAT_OPTIONS.find((o) => o.value === String(parseVatRate(item.tax_rate ?? defaultRate))) || null}
                                                    onChange={(opt) => updateRow(index, { tax_rate: `${opt.value}%` })}
                                                    isSearchable={false}
                                                    theme={brandSelectTheme}
                                                    styles={compactSelectStyles}
                                                    menuPortalTarget={portalTarget}
                                                    menuPlacement="auto"
                                                    classNamePrefix="react-select"
                                                />
                                            </td>
                                        )}
                                        <td className="col-amount num">
                                            <div className="inv-cell-amount">{money(lineAmount(item))}</div>
                                        </td>
                                        <td className="col-actions">
                                            <div className="inv-row-actions">
                                                <button
                                                    type="button"
                                                    className={`inv-icon-btn${expanded ? ' is-active' : ''}`}
                                                    aria-label={`Artikelkod och typ, rad ${index + 1}`}
                                                    aria-expanded={expanded}
                                                    title="Artikelkod och typ"
                                                    onClick={() => toggleDetails(item.id)}
                                                >
                                                    <SlidersHorizontal size={15} strokeWidth={2} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="inv-icon-btn inv-icon-btn--danger"
                                                    aria-label={`Ta bort rad ${index + 1}`}
                                                    title="Ta bort rad"
                                                    onClick={() => removeRow(index)}
                                                >
                                                    <Trash2 size={15} strokeWidth={2} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expanded && (
                                        <tr className="inv-table__details-row">
                                            <td colSpan={columnCount}>
                                                <div className="inv-table__details">
                                                    <div>
                                                        <label className="inv-label" htmlFor={`inv-row-code-${item.id}`}>Artikelkod</label>
                                                        <input
                                                            id={`inv-row-code-${item.id}`}
                                                            type="text"
                                                            className="inv-control inv-control--sm"
                                                            value={item.productcode || ''}
                                                            onChange={(e) => updateRow(index, { productcode: e.target.value })}
                                                            placeholder="T.ex. ART-001"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="inv-label" htmlFor={`inv-row-type-${item.id}`}>Artikeltyp</label>
                                                        <Select
                                                            inputId={`inv-row-type-${item.id}`}
                                                            options={TYPE_OPTIONS}
                                                            value={TYPE_OPTIONS.find((t) => t.value === normalizeType(item.type)) || TYPE_OPTIONS[0]}
                                                            onChange={(opt) => updateRow(index, { type: opt.value })}
                                                            isSearchable={false}
                                                            theme={brandSelectTheme}
                                                            styles={compactSelectStyles}
                                                            menuPortalTarget={portalTarget}
                                                            classNamePrefix="react-select"
                                                        />
                                                    </div>
                                                    {item._fromTimeImport && (
                                                        <div className="inv-help align-self-end pb-2">
                                                            <Clock size={12} strokeWidth={2} /> Importerad från tidrapport
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}

                    </tbody>
                </table>
            </div>

            <div className="inv-items-toolbar">
                <button type="button" className="inv-btn inv-btn--secondary" onClick={() => addRows([blankRow()])}>
                    <Plus strokeWidth={2} />
                    Ny rad
                </button>
                {canImportTime && (
                    <button type="button" className="inv-btn inv-btn--ghost" onClick={openTimeImport}>
                        <Clock strokeWidth={2} />
                        Importera från tidrapport
                    </button>
                )}
            </div>

            <div className="inv-items-footer">
                <div className="inv-items-footer__start">{footerStart}</div>
                {items.length > 0 && (
                    <div className="inv-totals">
                        <div className="inv-totals__row">
                            <span>{isTaxable ? 'Delsumma exkl. moms' : 'Delsumma'}</span>
                            <span className="inv-totals__value">{money(totals.subtotal)}</span>
                        </div>
                        {isTaxable && Object.entries(totals.taxGroups)
                            .sort(([a], [b]) => Number(b) - Number(a))
                            .map(([rate, group]) => (
                                <div className="inv-totals__row" key={rate}>
                                    <span>Moms {rate} %</span>
                                    <span className="inv-totals__value">{money(group.tax)}</span>
                                </div>
                            ))}
                        {roundTotal && Math.abs(totals.roundingAmount) > 0.001 && (
                            <div className="inv-totals__row">
                                <span>Öresavrundning</span>
                                <span className="inv-totals__value">{money(totals.roundingAmount)}</span>
                            </div>
                        )}
                        <div className="inv-totals__row inv-totals__row--total">
                            <span>{isTaxable ? 'Totalt inkl. moms' : 'Totalt'}</span>
                            <span className="inv-totals__value">{money(totals.finalTotal)}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoiceItemsTable;
